import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { compose } from 'redux';
import { useSelector } from 'react-redux';
import {
  Crown,
  Gift,
  Calendar,
  Trophy,
  Sparkles,
  Check,
  Lock,
  Newspaper,
  Megaphone,
  Star,
  X,
  ChevronRight,
  Loader2,
  Image as ImageIcon,
  Coins,
  HelpCircle,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  Badge,
  Button,
  Avatar,
  AvatarFallback,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@gu-corp/gu-ui';

import Link from 'next/link';
import Layout from '~/components/layout';
import Identicon from '~/components/identicon';
import {
  TierProgress,
  TierPassCard,
  TierGemIcon,
  TierLadder,
  CampaignBanner,
  CampaignSlider,
  EmailRegistrationDialog,
} from '~/components/joc-club';
import { getClubEmailStatus } from '~/services/joc-club-email';
import { useAccount, withAccount } from '~/hooks/with-account';
import { withApolloClient } from '~/graphql/client';
import { withLendingData, useLendingPlanData } from '~/hooks/with-lending-data';
import { withNftData, useNftData } from '~/hooks/with-nft-data';
import { renderBalance } from '~/utils/render.util';
import { truncateEthAddress } from '~/utils/string.utils';
import { AppRoutesEnum } from '~/enums/route.enum';
import { LendingPlanEnum } from '~/enums/lending-plan.enum';
import { getAssetTokensAction } from '~/store/actions/user-state-actions';
import { RootState } from '~/store/configure-store';
import {
  getTierFromBalance,
  JocClubTier,
  TIER_CONFIGS,
  TIER_ORDER,
  formatJocAmount,
} from '~/constants/joc-club';
import { ethers } from 'ethers';
import { getEnv } from '~/env';
import {
  useMemberInfo,
  useJocClubActions,
  JocClubActionError,
  notifyMemberUpdated,
} from '~/hooks/use-joc-club-contract';
import { getPublishedNews, getUpcomingEvents, getPublishedBenefits } from '~/services/joc-club';
import type { NewsItem, EventItem, BenefitItem } from '~/services/joc-club-admin';
import {
  getBenefitCategoryLabel,
  getEventTypeLabel,
  getNewsCategoryLabel,
  getTierLevelBadgeLabel,
} from '~/utils/joc-club-labels';
import { getLocalized } from '~/utils/localized';

const isJocClubEnabled = getEnv('NEXT_PUBLIC_FEATURE_JOC_CLUB_ENABLED') === 'true';
// Where to send holders with no JOC to acquire it.
const GET_JOC_URL = 'https://www.japanopenchain.org/investors/';
const isLocalDev = getEnv('NEXT_PUBLIC_JOC_URL')?.includes('localhost') || false;

// Ways to acquire JOC, transcribed from the joc-website investors page
// (src/pages/investors/index.js — buyPaths). URLs and copy mirror that source.
// Every card renders the same three lines: action headline → helper → supported.
type GetJocCategory = 'cex' | 'card' | 'dex';
const GET_JOC_OPTIONS: { id: string; category: GetJocCategory; url: string }[] = [
  { id: 'zaif', category: 'cex', url: 'https://zaif.jp/sp/instant_exchange/joc/buy' },
  { id: 'gateio', category: 'cex', url: 'https://www.gate.io/' },
  { id: 'mexc', category: 'cex', url: 'https://www.mexc.com/' },
  { id: 'bit2me', category: 'cex', url: 'https://bit2me.com/' },
  { id: 'alchemy', category: 'card', url: 'https://alchemypay.org/' },
  // X-Swap retired (service shut down) — entry removed.
  // JOCX (Uniswap on Ethereum) hidden for now — re-enable to restore.
  // { id: 'uniswap', category: 'dex', url: 'https://app.uniswap.org/swap?outputCurrency=0xbb1E1399EEE1f577F1B4359224155f5Db39CA084' },
];
const GET_JOC_CATEGORY_STYLE: Record<GetJocCategory, string> = {
  cex: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  card: 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300',
  dex: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
};

// Colored pill per feed category (falls back to muted for unknown categories).
const NEWS_CATEGORY_STYLE: Record<string, string> = {
  newsletter: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  voice_memo: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  digest: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  tweet: 'bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300',
  ama: 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300',
};

function NewsCategoryBadge({ t, category }: { t: any; category: string }) {
  const style = NEWS_CATEGORY_STYLE[category] || 'bg-muted text-muted-foreground';
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${style}`}
    >
      {getNewsCategoryLabel(t, category)}
    </span>
  );
}

// Tier level to JocClubTier mapping for benefit comparison
const TIER_LEVEL_ORDER: Record<string, number> = {
  white: 1,
  bronze: 2,
  silver: 3,
  gold: 4,
  platinum: 5,
};
const JOC_CLUB_TIER_ORDER: Record<string, number> = {
  [JocClubTier.NONE]: 0,
  [JocClubTier.BRONZE]: 2,
  [JocClubTier.SILVER]: 3,
  [JocClubTier.GOLD]: 4,
  [JocClubTier.PLATINUM]: 5,
};

function isBenefitUnlocked(benefitMinTier: string, currentTier: JocClubTier): boolean {
  return (JOC_CLUB_TIER_ORDER[currentTier] || 0) >= (TIER_LEVEL_ORDER[benefitMinTier] || 0);
}

// Feed/content visibility: `white` is public (visible to everyone, incl. no JOC);
// higher tiers are gated and shown as locked teasers below that tier.
function isContentUnlocked(minTier: string, currentTier: JocClubTier): boolean {
  return minTier === 'white' || isBenefitUnlocked(minTier, currentTier);
}

// Icon mapping from string name to component
const ICON_MAP: Record<string, React.ReactNode> = {
  newspaper: <Newspaper className="w-5 h-5" />,
  gift: <Gift className="w-5 h-5" />,
  trophy: <Trophy className="w-5 h-5" />,
  sparkles: <Sparkles className="w-5 h-5" />,
  crown: <Crown className="w-5 h-5" />,
  star: <Star className="w-5 h-5" />,
  calendar: <Calendar className="w-5 h-5" />,
  megaphone: <Megaphone className="w-5 h-5" />,
};

const Dashboard = () => {
  const { t, i18n } = useTranslation('common');
  const { account } = useAccount();
  const [activeTab, setActiveTab] = useState<string>('news');
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState<JocClubTier | null>(null);
  const [showGetJocModal, setShowGetJocModal] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailCheckPending, setEmailCheckPending] = useState(false);

  // Contract integration
  const { member, refetch: refetchMember, tierName } = useMemberInfo(account?.ethAddress);
  const { join, leave, pending: txPending } = useJocClubActions();

  // Email gate (#619): a verified email is required before minting the status
  // NFT. Fail closed — when the status check errors, ask for the email.
  const onClickJoin = useCallback(async () => {
    setEmailCheckPending(true);
    try {
      const status = await getClubEmailStatus();
      if (status.emailVerified) {
        setShowJoinModal(true);
      } else {
        setShowEmailDialog(true);
      }
    } catch {
      setShowEmailDialog(true);
    } finally {
      setEmailCheckPending(false);
    }
  }, []);

  // Map a join/addStake/leave failure to a translated, user-facing message.
  // ALREADY_MEMBER/NOT_MEMBER mean our cached member state is stale (e.g. a
  // membership change from another tab or session) — refetch so the UI
  // self-heals instead of only telling the user to reload.
  const getActionErrorMessage = useCallback(
    (e: any): string => {
      const code = e instanceof JocClubActionError ? e.code : undefined;
      if (code === 'ALREADY_MEMBER' || code === 'NOT_MEMBER') {
        // Our cached member state was stale — resync every useMemberInfo
        // instance (this card, the sidebar mini-pass, ...), not just this one.
        notifyMemberUpdated();
      }
      switch (code) {
        case 'ALREADY_MEMBER':
          return t('joc_club.error_already_member');
        case 'NOT_MEMBER':
          return t('joc_club.error_not_member');
        case 'INSUFFICIENT_AMOUNT':
          return t('joc_club.error_insufficient_amount');
        case 'SOULBOUND_TRANSFER':
          return t('joc_club.error_soulbound_transfer');
        case 'REJECTED':
          return t('joc_club.error_rejected');
        case 'UNKNOWN':
          return t('joc_club.error_unknown');
        default:
          return e?.message || t('joc_club.error_unknown');
      }
    },
    [t],
  );

  // NFT data
  const { nfts, loading: nftLoading } = useNftData();
  const nftCount = nfts?.length ?? 0;

  // Token data
  const assetTokens = useSelector((state: RootState) => state.userState?.assetTokens);
  const isLoadingTokens = useSelector(getAssetTokensAction.isPending);
  const tokenCount = assetTokens?.length ?? 0;

  // Lending data
  const { data: seedData } = useLendingPlanData(LendingPlanEnum.SEED);
  const { data: sproutData } = useLendingPlanData(LendingPlanEnum.SPROUT);
  const { data: harvestData } = useLendingPlanData(LendingPlanEnum.HARVEST);

  const { lendingLoading, lendingTotal } = useMemo(() => {
    if (seedData && sproutData && harvestData) {
      const allLended = [...seedData.records, ...sproutData.records, ...harvestData.records];
      let total = BigInt(0);
      allLended.forEach((item) => {
        if (item.withdrawn === null) {
          total = total + BigInt(item.amount);
        }
      });
      return { lendingLoading: false, lendingTotal: total };
    }
    return { lendingLoading: true, lendingTotal: BigInt(0) };
  }, [harvestData, seedData, sproutData]);

  const currentTier = getTierFromBalance(account.balance);
  const isMember = currentTier !== JocClubTier.NONE;
  const isTierMember = member && member.tier > 0;
  const totalJocHeld =
    (account.balance ? Number(account.balance / BigInt(10 ** 18)) : 0) +
    (member ? Number(member.lockedAmount / BigInt(10 ** 18)) : 0);

  // Compact asset row (shared between full and fallback render)
  const assetRow = (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <a href="https://www.japanopenchain.org/joc-club" target="_blank" rel="noopener noreferrer">
        <div className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer min-w-0">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <HelpCircle className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground truncate">{t('about_joc_club')}</p>
            <p className="text-sm font-bold truncate">{t('about_joc_club_desc')}</p>
          </div>
        </div>
      </a>
      <Link href={AppRoutesEnum.WALLET}>
        <div className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer min-w-0">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Coins className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">{t('tokens')}</p>
            {isLoadingTokens ? (
              <Skeleton className="h-5 w-12" />
            ) : (
              <p className="text-sm font-bold">
                {tokenCount} {t('types')}
              </p>
            )}
          </div>
        </div>
      </Link>
      <Link href={AppRoutesEnum.NFTS}>
        <div className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer min-w-0">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <ImageIcon className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">NFT</p>
            {nftLoading ? (
              <Skeleton className="h-5 w-12" />
            ) : (
              <p className="text-sm font-bold">
                {nftCount} {t('items')}
              </p>
            )}
          </div>
        </div>
      </Link>
    </div>
  );

  // When JOC Club is disabled, render a minimal fallback (no redirect to avoid loop on home).
  if (!isJocClubEnabled) {
    return (
      <Layout>
        <div className="space-y-6">{assetRow}</div>
      </Layout>
    );
  }

  const tabItems = [
    { value: 'news', label: t('joc_club.tab_news'), icon: <Newspaper className="w-4 h-4" /> },
    {
      value: 'campaigns',
      label: t('joc_club.tab_campaigns'),
      icon: <Megaphone className="w-4 h-4" />,
    },
    { value: 'events', label: t('joc_club.tab_events'), icon: <Calendar className="w-4 h-4" /> },
    { value: 'benefits', label: t('joc_club.tab_benefits'), icon: <Gift className="w-4 h-4" /> },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <h1 className="text-2xl font-bold">{t('joc_club.welcome_back')} 👋</h1>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-start">
          {/* Center column: campaign banner + assets + tabs */}
          <div className="order-2 min-w-0 space-y-6 xl:order-1">
            <CampaignBanner onDetail={() => setActiveTab('campaigns')} />

            {assetRow}

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList variant="underline" alignment="start">
                {tabItems.map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    variant="underline"
                    className="gap-2"
                  >
                    {tab.icon}
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              <div className="mt-6 min-h-[400px]">
                <TabsContent value="news">
                  <NewsTab t={t} i18n={i18n} currentTier={currentTier} />
                </TabsContent>
                <TabsContent value="campaigns">
                  <CampaignSlider />
                </TabsContent>
                <TabsContent value="events">
                  <EventsTab t={t} i18n={i18n} />
                </TabsContent>
                <TabsContent value="benefits">
                  <BenefitsTab t={t} i18n={i18n} currentTier={currentTier} isMember={isMember} />
                </TabsContent>
              </div>
            </Tabs>
          </div>

          {/* Right column: status card + tier ladder. Below xl (no rail) the
              pass card is width-capped, so sit the ladder beside it to use the
              free space; in the xl rail they stack. */}
          <div className="order-1 grid grid-cols-1 items-start gap-4 md:grid-cols-2 xl:order-2 xl:grid-cols-1">
            {/* Status Card */}
            {(() => {
              const walletJoc = account.balance ? Number(account.balance / BigInt(10 ** 18)) : 0;
              const lockedJoc = member ? Number(member.lockedAmount / BigInt(10 ** 18)) : 0;
              const totalJoc = walletJoc + lockedJoc;

              if (isTierMember && member) {
                return (
                  <TierPassCard
                    tier={tierName}
                    lockedJoc={ethers.formatEther(member.lockedAmount)}
                    joinedAt={member.joinedAt}
                    address={account?.ethAddress || ''}
                    onLeave={async () => {
                      if (!confirm(t('joc_club.leave_confirm'))) return;
                      try {
                        await leave();
                        refetchMember();
                      } catch (e: any) {
                        alert(getActionErrorMessage(e));
                      }
                    }}
                    leaveDisabled={txPending}
                    onGetJoc={() => setShowGetJocModal(true)}
                  />
                );
              }

              // No status: holds less than 1 JOC — not a member. Prompt to acquire JOC.
              if (totalJoc < 1) {
                return (
                  <Card className="relative overflow-hidden rounded-2xl border py-0 shadow-sm">
                    <div
                      aria-hidden
                      className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-gradient-to-br from-primary/20 via-primary/5 to-transparent blur-2xl"
                    />
                    <CardContent className="relative flex flex-col p-4">
                      <div className="flex items-center gap-2.5">
                        {account?.ethAddress ? (
                          <Identicon
                            address={account.ethAddress}
                            size={36}
                            className="border shadow-sm"
                          />
                        ) : (
                          <Avatar className="h-9 w-9 border shadow-sm">
                            <AvatarFallback className="bg-muted" />
                          </Avatar>
                        )}
                        <div className="min-w-0">
                          <p className="text-base font-bold leading-tight">
                            {t('joc_club.no_status')}
                          </p>
                          {account?.ethAddress && (
                            <p className="font-mono text-xs text-muted-foreground">
                              {truncateEthAddress(account.ethAddress)}
                            </p>
                          )}
                        </div>
                      </div>

                      <p className="mt-3 line-clamp-2 text-xs text-muted-foreground">
                        {t('joc_club.no_status_desc')}
                      </p>

                      <div className="mt-auto flex items-center justify-between gap-3 border-t pt-3">
                        <a
                          href="https://www.japanopenchain.org/joc-club"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-medium text-muted-foreground hover:text-foreground"
                        >
                          {t('about_joc_club')}
                        </a>
                        <Button
                          size="sm"
                          onClick={() => setShowGetJocModal(true)}
                          className="shrink-0"
                        >
                          <Coins className="w-4 h-4" />
                          {t('joc_club.get_joc')}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              }

              // Non-member (white member) view
              return (
                <Card className="relative overflow-hidden rounded-2xl border py-0 shadow-sm">
                  {/* Decorative gradient wash */}
                  <div
                    aria-hidden
                    className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-gradient-to-br from-primary/20 via-primary/5 to-transparent blur-2xl"
                  />
                  <CardContent className="relative flex flex-col p-4">
                    {/* Header: identity + join action */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2.5">
                        {account?.ethAddress ? (
                          <Identicon
                            address={account.ethAddress}
                            size={36}
                            className="border shadow-sm"
                          />
                        ) : (
                          <Avatar className="h-9 w-9 border shadow-sm">
                            <AvatarFallback className="bg-muted" />
                          </Avatar>
                        )}
                        <div className="min-w-0">
                          <p className="text-base font-bold leading-tight">
                            {t('joc_club.basic_member')}
                          </p>
                          {account?.ethAddress && (
                            <p className="font-mono text-xs text-muted-foreground">
                              {truncateEthAddress(account.ethAddress)}
                            </p>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={onClickJoin}
                        disabled={
                          !account.balance || account.balance === BigInt(0) || emailCheckPending
                        }
                        className="shrink-0"
                      >
                        {t('joc_club.lock_and_join')}
                      </Button>
                    </div>

                    {/* Balance */}
                    <div className="mt-4">
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        {t('joc_club.wallet_balance')}
                      </p>
                      <p className="mt-0.5 text-2xl font-bold tracking-tight">
                        {formatJocAmount(totalJoc)}{' '}
                        <span className="text-sm font-semibold text-muted-foreground">JOC</span>
                      </p>
                    </div>

                    {/* Progress bar with locked portion */}
                    <TierProgress
                      className="mt-1.5"
                      balanceWei={BigInt(totalJoc) * BigInt(10 ** 18)}
                      lockedWei={member?.lockedAmount}
                      showDetails={true}
                    />

                    {/* Footer: hint caption + get JOC */}
                    <div className="mt-auto flex items-center justify-between gap-3 border-t pt-3">
                      <p className="text-xs font-medium text-muted-foreground">
                        {t('joc_club.become_tier_member')}
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowGetJocModal(true)}
                        className="shrink-0 text-xs font-medium text-primary hover:underline"
                      >
                        {t('joc_club.get_joc')}
                      </button>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Tier ladder is hidden on mobile (tracked as a separate issue). */}
            <TierLadder totalJoc={totalJocHeld} className="hidden md:block" />
          </div>
        </div>

        {/* Email registration gate (#619) */}
        {showEmailDialog && (
          <EmailRegistrationDialog
            onClose={() => setShowEmailDialog(false)}
            onVerified={() => {
              setShowEmailDialog(false);
              setShowJoinModal(true);
            }}
          />
        )}

        {/* Join Modal */}
        {showJoinModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setShowJoinModal(false)}
          >
            <Card className="w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{t('joc_club.join_modal_title')}</CardTitle>
                  <button
                    onClick={() => setShowJoinModal(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <CardDescription>{t('joc_club.join_modal_desc')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Tier selection */}
                <div className="space-y-2">
                  {TIER_ORDER.filter((tier) => tier !== JocClubTier.NONE).map((tier) => {
                    const config = TIER_CONFIGS[tier];
                    const name = i18n.language === 'ja' ? config.nameJa : config.name;
                    const balanceJoc = account.balance
                      ? Number(account.balance / BigInt(10 ** 18))
                      : 0;
                    const canAfford = balanceJoc >= config.minJoc;
                    const isSelected = selectedTier === tier;

                    return (
                      <button
                        key={tier}
                        onClick={() => canAfford && setSelectedTier(tier)}
                        disabled={!canAfford}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                          isSelected
                            ? `border-2 ${config.borderColor} ${config.bgColor}`
                            : canAfford
                            ? 'border hover:border-primary/50 hover:bg-muted/30'
                            : 'border border-dashed opacity-50 cursor-not-allowed'
                        }`}
                      >
                        <TierGemIcon tier={tier} className="h-9 w-9 shrink-0" />
                        <div className="flex-1">
                          <p className={`font-medium ${isSelected ? config.color : ''}`}>{name}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatJocAmount(config.minJoc)} JOC {t('joc_club.lock_required')}
                          </p>
                        </div>
                        {isSelected && <Check className="w-5 h-5 text-primary" />}
                        {!canAfford && <Lock className="w-4 h-4 text-muted-foreground" />}
                      </button>
                    );
                  })}
                </div>

                <p className="text-xs text-muted-foreground">{t('joc_club.lock_note')}</p>

                <button
                  onClick={async () => {
                    if (!selectedTier) return;
                    const amount = String(TIER_CONFIGS[selectedTier].minJoc);
                    try {
                      await join(amount);
                      setShowJoinModal(false);
                      setSelectedTier(null);
                      refetchMember();
                    } catch (e: any) {
                      alert(getActionErrorMessage(e));
                    }
                  }}
                  disabled={txPending || !selectedTier}
                  className="w-full py-3 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 font-medium"
                >
                  {txPending ? (
                    <span className="shimmer">{t('joc_club.processing') || 'Processing...'}</span>
                  ) : selectedTier ? (
                    t('joc_club.join_as_tier', {
                      tier:
                        i18n.language === 'ja'
                          ? TIER_CONFIGS[selectedTier].nameJa
                          : TIER_CONFIGS[selectedTier].name,
                      amount: formatJocAmount(TIER_CONFIGS[selectedTier].minJoc),
                    })
                  ) : (
                    t('joc_club.select_tier')
                  )}
                </button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Get JOC Modal */}
        {showGetJocModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowGetJocModal(false)}
          >
            <Card
              className="flex max-h-[90vh] w-full max-w-4xl flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{t('joc_club.get_joc_modal_title')}</CardTitle>
                  <button
                    onClick={() => setShowGetJocModal(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <CardDescription>{t('joc_club.get_joc_modal_desc')}</CardDescription>
              </CardHeader>
              <CardContent className="overflow-y-auto scrollbar-hide">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {GET_JOC_OPTIONS.map((option) => (
                    <a
                      key={option.id}
                      href={option.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col gap-2 rounded-lg border p-4 transition-all hover:border-primary/50 hover:bg-muted/30"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className={`w-fit rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                            GET_JOC_CATEGORY_STYLE[option.category]
                          }`}
                        >
                          {t(`joc_club.get_joc_category_${option.category}`)}
                        </span>
                        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium">{t(`joc_club.get_joc_action_${option.id}`)}</p>
                        <p className="text-xs text-muted-foreground">
                          {t(`joc_club.get_joc_helper_${option.id}`)}
                        </p>
                      </div>
                      <p className="mt-auto text-xs font-medium">
                        {t(`joc_club.get_joc_supported_${option.id}`)}
                      </p>
                    </a>
                  ))}
                </div>

                {/* Notes */}
                <ul className="mt-4 space-y-1.5 border-t pt-4 text-xs text-muted-foreground">
                  <li className="flex gap-1.5">
                    <span aria-hidden>・</span>
                    <span>{t('joc_club.get_joc_note_cex')}</span>
                  </li>
                  <li className="flex gap-1.5">
                    <span aria-hidden>・</span>
                    <span>{t('joc_club.get_joc_note_card')}</span>
                  </li>
                  <li className="flex gap-1.5">
                    <span aria-hidden>・</span>
                    <span>{t('joc_club.get_joc_note_dex')}</span>
                  </li>
                </ul>

                <p className="pt-3 text-xs text-muted-foreground">
                  {t('joc_club.get_joc_modal_footer')}{' '}
                  <a
                    href={GET_JOC_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {GET_JOC_URL}
                  </a>
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      {/* Dev: Faucet bar (Hardhat only) */}
      {isLocalDev && account?.ethAddress && (
        <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center gap-2 px-4 py-2 bg-yellow-100/95 dark:bg-yellow-950/95 border-t border-yellow-400 backdrop-blur-sm">
          <span className="text-xs text-yellow-700 dark:text-yellow-400 font-mono font-bold">
            DEV
          </span>
          {[100, 1000, 5000, 50000].map((amount) => (
            <button
              key={amount}
              onClick={async () => {
                const hex = '0x' + (BigInt(amount) * BigInt(10 ** 18)).toString(16);
                const provider = new ethers.JsonRpcProvider(getEnv('NEXT_PUBLIC_JOC_URL'));
                await provider.send('hardhat_setBalance', [account.ethAddress, hex]);
                window.location.reload();
              }}
              className="px-3 py-1 text-xs bg-yellow-200 dark:bg-yellow-800 rounded hover:bg-yellow-300 dark:hover:bg-yellow-700"
            >
              +{formatJocAmount(amount)} JOC
            </button>
          ))}
          <span className="ml-auto text-xs text-yellow-600 dark:text-yellow-500 font-mono truncate">
            {account.ethAddress}
          </span>
        </div>
      )}
    </Layout>
  );
};

// --- Tab Components ---

function EmptyState({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center border border-border rounded-lg bg-muted/10">
      <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-4">
        {icon}
      </div>
      <p className="text-base font-medium text-foreground mb-1">{title}</p>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
    </div>
  );
}

function NewsTab({ t, i18n, currentTier }: { t: any; i18n: any; currentTier: JocClubTier }) {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<NewsItem | null>(null);
  const siteLocale = i18n.language;

  useEffect(() => {
    getPublishedNews()
      .then(setItems)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="grid gap-4 md:grid-cols-2">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <Skeleton className="h-32 w-full rounded-lg mb-3" />
              <Skeleton className="h-4 w-3/4 mb-2" />
              <Skeleton className="h-3 w-1/2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );

  if (items.length === 0) {
    return (
      <EmptyState
        icon={<Newspaper className="w-7 h-7 text-muted-foreground" />}
        title={t('joc_club.news_coming_soon')}
      />
    );
  }

  // Detail view
  if (selected) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => setSelected(null)}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          {t('joc_club.back_to_list')}
        </button>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Badge className="text-xs">{getNewsCategoryLabel(t, selected.category)}</Badge>
              <span className="text-xs text-muted-foreground">
                {selected.publishedAt ? new Date(selected.publishedAt).toLocaleDateString() : ''}
              </span>
            </div>
            <h2 className="text-xl font-bold mb-4">{getLocalized(selected.title, siteLocale)}</h2>
            {selected.imageUrl && (
              <img
                src={selected.imageUrl}
                alt=""
                className="w-full rounded-xl mb-6 max-h-72 object-cover"
              />
            )}
            <div className="prose prose-sm max-w-none whitespace-pre-wrap text-foreground/80">
              {getLocalized(selected.body, siteLocale)}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-3">
      {items.map((item) => {
        const title = getLocalized(item.title, siteLocale);
        const body = getLocalized(item.body, siteLocale);
        const date = item.publishedAt ? new Date(item.publishedAt).toLocaleDateString() : '';
        const unlocked = isContentUnlocked(item.minTier, currentTier);

        // Locked teaser: title/category/date visible, body hidden behind a tier gate.
        if (!unlocked) {
          return (
            <Card key={item.id} className="border-dashed py-0">
              <CardContent className="p-0">
                <div className="flex items-stretch">
                  {item.imageUrl && (
                    <div className="relative w-28 sm:w-36 shrink-0 overflow-hidden rounded-l-lg">
                      <img src={item.imageUrl} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-background/40 backdrop-blur-[2px]" />
                    </div>
                  )}
                  <div className="flex-1 p-4 flex flex-col justify-center min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <NewsCategoryBadge t={t} category={item.category} />
                      <span className="text-[11px] text-muted-foreground">{date}</span>
                    </div>
                    <h3 className="font-semibold text-base leading-snug line-clamp-2 text-muted-foreground">
                      {title || '—'}
                    </h3>
                    <p className="mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground">
                      <Lock className="w-3 h-3 shrink-0" />
                      {t('joc_club.feed_locked', {
                        tier: getTierLevelBadgeLabel(t, item.minTier),
                      })}
                    </p>
                  </div>
                  <div className="flex items-center pr-4 shrink-0">
                    <Lock className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        }

        return (
          <Card
            key={item.id}
            className="group cursor-pointer py-0 hover:border-primary/50 hover:shadow-sm transition-all"
            onClick={() => setSelected(item)}
          >
            <CardContent className="p-0">
              <div className="flex items-stretch">
                {item.imageUrl && (
                  <div className="w-28 sm:w-36 shrink-0 overflow-hidden rounded-l-lg">
                    <img
                      src={item.imageUrl}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                <div className="flex-1 p-4 flex flex-col justify-center min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <NewsCategoryBadge t={t} category={item.category} />
                    <span className="text-[11px] text-muted-foreground">{date}</span>
                  </div>
                  <h3 className="font-semibold text-base leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                    {title || '—'}
                  </h3>
                  {body && (
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-1">{body}</p>
                  )}
                </div>
                <div className="flex items-center pr-4 shrink-0">
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function EventsTab({ t, i18n }: { t: any; i18n: any }) {
  const [items, setItems] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const siteLocale = i18n.language;

  useEffect(() => {
    getUpcomingEvents()
      .then(setItems)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4 flex items-center gap-4">
              <Skeleton className="w-14 h-14 rounded-xl shrink-0" />
              <div className="flex-1">
                <Skeleton className="h-4 w-2/3 mb-2" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );

  if (items.length === 0) {
    return (
      <EmptyState
        icon={<Calendar className="w-7 h-7 text-muted-foreground" />}
        title={t('joc_club.events_coming_soon')}
      />
    );
  }

  return (
    <div className="space-y-3">
      {items.map((item) => {
        const eventDate = item.eventDate ? new Date(item.eventDate) : null;
        const month = eventDate?.toLocaleDateString(siteLocale, { month: 'short' }).toUpperCase();
        const day = eventDate?.getDate();

        return (
          <Card key={item.id} className="hover:border-primary/50 hover:shadow-sm transition-all">
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                {/* Calendar date badge */}
                <div className="w-14 h-14 rounded-xl border-2 border-primary/20 bg-primary/5 flex flex-col items-center justify-center shrink-0">
                  <span className="text-[10px] font-bold text-primary leading-none">{month}</span>
                  <span className="text-xl font-bold text-primary leading-tight">{day}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-semibold text-sm">
                      {getLocalized(item.title, siteLocale)}
                    </h3>
                    <Badge variant="outline" className="text-[10px]">
                      {getEventTypeLabel(t, item.type)}
                    </Badge>
                    <Badge variant="outline" className="text-[10px]">
                      {getTierLevelBadgeLabel(t, item.minTier)}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                    {getLocalized(item.description, siteLocale)}
                  </p>
                  {item.location && (
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      <Sparkles className="w-3 h-3" />
                      <span>{item.location}</span>
                    </div>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-2" />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function BenefitsTab({
  t,
  i18n,
  currentTier,
  isMember,
}: {
  t: any;
  i18n: any;
  currentTier: JocClubTier;
  isMember: boolean;
}) {
  const [items, setItems] = useState<BenefitItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<BenefitItem | null>(null);
  const siteLocale = i18n.language;

  useEffect(() => {
    getPublishedBenefits()
      .then(setItems)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="grid gap-4 md:grid-cols-2">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4 flex items-start gap-3">
              <Skeleton className="w-10 h-10 rounded-xl shrink-0" />
              <div className="flex-1">
                <Skeleton className="h-4 w-2/3 mb-2" />
                <Skeleton className="h-3 w-full" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );

  if (items.length === 0) {
    return (
      <EmptyState
        icon={<Gift className="w-7 h-7 text-muted-foreground" />}
        title={t('joc_club.no_benefits')}
      />
    );
  }

  // Detail view
  if (selected) {
    const isUnlocked = isBenefitUnlocked(selected.minTier, currentTier);
    const icon = ICON_MAP[selected.icon] || <Gift className="w-5 h-5" />;

    return (
      <div className="space-y-4">
        <button
          onClick={() => setSelected(null)}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          {t('joc_club.back_to_list')}
        </button>
        <Card className={isUnlocked ? 'border-green-200 dark:border-green-800' : ''}>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`p-3 rounded-xl ${
                  isUnlocked
                    ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {icon}
              </div>
              <div>
                <h2 className="text-lg font-bold">{getLocalized(selected.title, siteLocale)}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant={isUnlocked ? 'outline' : 'secondary'} className="text-xs">
                    {getTierLevelBadgeLabel(t, selected.minTier)}
                  </Badge>
                  {isUnlocked ? (
                    <Badge className="text-xs bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 border-0">
                      <Check className="w-3 h-3 mr-1" />
                      {t('joc_club.unlocked') || 'Unlocked'}
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">
                      <Lock className="w-3 h-3 mr-1" />
                      {t('joc_club.locked') || 'Locked'}
                    </Badge>
                  )}
                  {selected.category === 'consumable' && (
                    <Badge variant="outline" className="text-xs">
                      {getBenefitCategoryLabel(t, selected.category)}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="prose prose-sm max-w-none whitespace-pre-wrap text-foreground/80">
              {getLocalized(selected.description, siteLocale)}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Grid view
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {items.map((benefit) => {
        const isUnlocked = isBenefitUnlocked(benefit.minTier, currentTier);
        const icon = ICON_MAP[benefit.icon] || <Gift className="w-5 h-5" />;

        return (
          <Card
            key={benefit.id}
            className={`cursor-pointer transition-all ${
              isUnlocked
                ? 'border-green-200 dark:border-green-800 hover:shadow-sm hover:border-green-300 dark:hover:border-green-700'
                : 'opacity-70 border-dashed hover:opacity-90'
            }`}
            onClick={() => setSelected(benefit)}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div
                  className={`p-2.5 rounded-xl shrink-0 ${
                    isUnlocked
                      ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3
                      className={`font-semibold text-sm ${
                        isUnlocked ? '' : 'text-muted-foreground'
                      }`}
                    >
                      {getLocalized(benefit.title, siteLocale)}
                    </h3>
                    {isUnlocked ? (
                      <Check className="w-4 h-4 text-green-500 shrink-0" />
                    ) : (
                      <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                    {getLocalized(benefit.description, siteLocale)}
                  </p>
                  <div className="flex items-center gap-2">
                    <Badge variant={isUnlocked ? 'outline' : 'secondary'} className="text-[10px]">
                      {getTierLevelBadgeLabel(t, benefit.minTier)}
                    </Badge>
                    {benefit.category === 'consumable' && (
                      <Badge variant="outline" className="text-[10px]">
                        {getBenefitCategoryLabel(t, benefit.category)}
                      </Badge>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-2" />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default compose(withApolloClient, withAccount, withLendingData, withNftData)(Dashboard);
